export const CREST_APP_UPDATE_AVAILABLE_EVENT = 'crest:app-update-available';

export type CrestBuildVersionInfo = {
  version?: number;
  buildId: string;
  builtAt?: string;
  gitSha?: string | null;
};

export type CrestAppKind = 'crest' | 'crm' | 'erp' | 'sap';

export type CrestBuildVersionTarget = {
  appKind: CrestAppKind;
  appNameZh: string;
  appNameEn: string;
  versionUrl: string;
};

export type CrestAppUpdateAvailableDetail = {
  appKind: CrestAppKind;
  appNameZh: string;
  appNameEn: string;
  versionUrl: string;
  currentBuildId: string;
  currentGitSha: string | null;
  next: CrestBuildVersionInfo;
};

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const INITIAL_CHECK_DELAY_MS = 30 * 1000;

let installed = false;
let pendingUpdate: CrestAppUpdateAvailableDetail | null = null;
let dispatchedUpdateKey: string | null = null;

export const CREST_CURRENT_BUILD_ID =
  typeof __CREST_BUILD_ID__ === 'string' && __CREST_BUILD_ID__ ? __CREST_BUILD_ID__ : 'local';
export const CREST_CURRENT_GIT_SHA =
  typeof __CREST_GIT_SHA__ === 'string' && __CREST_GIT_SHA__ ? __CREST_GIT_SHA__ : null;

function currentWebProject(): string | undefined {
  if (typeof __CREST_WEB_PROJECT__ !== 'undefined') return __CREST_WEB_PROJECT__;
  return undefined;
}

export function installBuildVersionRefreshMonitor() {
  if (installed || !import.meta.env.PROD || typeof window === 'undefined') return;
  installed = true;

  window.setTimeout(() => void checkForBuildUpdate(), INITIAL_CHECK_DELAY_MS);
  window.setInterval(() => void checkForBuildUpdate(), CHECK_INTERVAL_MS);
  window.addEventListener('focus', handleWakeup);
  window.addEventListener('online', handleWakeup);
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

export async function checkForBuildUpdate(fetcher: typeof fetch = fetch) {
  try {
    const target = resolveBuildVersionTarget();
    const version = await fetchBuildVersion(fetcher, target.versionUrl);
    if (!version) return;
    if (!isNewerBuildVersion(version)) {
      pendingUpdate = null;
      return;
    }
    pendingUpdate = {
      ...target,
      currentBuildId: CREST_CURRENT_BUILD_ID,
      currentGitSha: CREST_CURRENT_GIT_SHA,
      next: version,
    };
    dispatchPendingUpdateIfVisible();
  } catch {
    // 版本检查不能影响主业务；网络失败时等下一轮轮询或用户回到页面再检查。
  }
}

export function getBuildVersionIdentity(version: CrestBuildVersionInfo) {
  const gitSha = version.gitSha?.trim();
  return gitSha ? `git:${gitSha}` : `build:${version.buildId}`;
}

export function isNewerBuildVersion(version: CrestBuildVersionInfo) {
  if (CREST_CURRENT_GIT_SHA && version.gitSha) {
    return version.gitSha !== CREST_CURRENT_GIT_SHA;
  }
  return version.buildId !== CREST_CURRENT_BUILD_ID;
}

export function resolveBuildVersionTarget(
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
  webProject = currentWebProject(),
): CrestBuildVersionTarget {
  const projectTarget = resolveBuildVersionTargetByProject(webProject);
  if (projectTarget) return projectTarget;

  if (
    pathname === '/crm' ||
    pathname.startsWith('/crm/') ||
    pathname.startsWith('/app/crm') ||
    pathname.startsWith('/app/crm')
  ) {
    return {
      appKind: 'crm',
      appNameZh: '销售中心',
      appNameEn: 'Sales Center',
      versionUrl: '/crm/version.json',
    };
  }
  if (
    pathname === '/erp' ||
    pathname.startsWith('/erp/') ||
    pathname.startsWith('/app/finance') ||
    pathname.startsWith('/app/erp')
  ) {
    return {
      appKind: 'erp',
      appNameZh: '财务中心',
      appNameEn: 'Finance Center',
      versionUrl: '/erp/version.json',
    };
  }
  return {
    appKind: 'crest',
    appNameZh: 'CREST平台',
    appNameEn: 'CREST Platform',
    versionUrl: '/version.json',
  };
}

function resolveBuildVersionTargetByProject(
  webProject: string | undefined,
): CrestBuildVersionTarget | null {
  if (webProject === 'crm-web') {
    return {
      appKind: 'crm',
      appNameZh: '销售中心',
      appNameEn: 'Sales Center',
      versionUrl: '/crm/version.json',
    };
  }
  if (webProject === 'erp-web') {
    return {
      appKind: 'erp',
      appNameZh: '财务中心',
      appNameEn: 'Finance Center',
      versionUrl: '/erp/version.json',
    };
  }
  if (webProject === 'sap-web') {
    return {
      appKind: 'sap',
      appNameZh: 'SAP中心',
      appNameEn: 'SAP Center',
      versionUrl: '/sap/version.json',
    };
  }
  if (webProject === 'crest-web') {
    return {
      appKind: 'crest',
      appNameZh: 'CREST平台',
      appNameEn: 'CREST Platform',
      versionUrl: '/version.json',
    };
  }
  return null;
}

async function fetchBuildVersion(
  fetcher: typeof fetch,
  versionUrl: string,
): Promise<CrestBuildVersionInfo | null> {
  const response = await fetcher(`${versionUrl}?t=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'same-origin',
  });
  if (!response.ok) return null;
  const data = (await response.json()) as Partial<CrestBuildVersionInfo>;
  if (!data || typeof data.buildId !== 'string' || data.buildId.trim() === '') return null;
  return {
    version: typeof data.version === 'number' ? data.version : undefined,
    buildId: data.buildId,
    builtAt: typeof data.builtAt === 'string' ? data.builtAt : undefined,
    gitSha: typeof data.gitSha === 'string' && data.gitSha.trim() ? data.gitSha.trim() : null,
  };
}

function handleWakeup() {
  dispatchPendingUpdateIfVisible();
  void checkForBuildUpdate();
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    handleWakeup();
  }
}

function dispatchPendingUpdateIfVisible() {
  if (!pendingUpdate || document.visibilityState !== 'visible') return;
  const updateKey = `${pendingUpdate.appKind}:${getBuildVersionIdentity(pendingUpdate.next)}`;
  if (dispatchedUpdateKey === updateKey) return;

  dispatchedUpdateKey = updateKey;
  window.dispatchEvent(
    new CustomEvent<CrestAppUpdateAvailableDetail>(CREST_APP_UPDATE_AVAILABLE_EVENT, {
      detail: pendingUpdate,
    }),
  );
}
